import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiShoppingBag, FiMenu, FiX, FiUser } = FiIcons;

export default function Navbar({ cartCount, onCartOpen }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Shop', href: '#shop' },
    { name: 'The Loop', href: '#how-it-works' },
    { name: 'Impact', href: '#impact' },
    { name: 'Guides', href: '#guides' },
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed w-full z-50 transition-all duration-500 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm py-3' : 'bg-transparent py-6'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex justify-between items-center">
        <button className="md:hidden p-2 -ml-2" onClick={() => setIsMobileMenuOpen(true)}>
          <SafeIcon icon={FiMenu} className="text-2xl" />
        </button>

        <a href="#" className="text-2xl md:text-3xl font-bold tracking-tighter text-[var(--navy)]">
          CleanNest<span className="text-[var(--mint-dark)]">.</span>
        </a>

        <div className="hidden md:flex gap-10 font-medium text-[11px] tracking-[0.2em] uppercase text-[var(--navy)]">
          {navLinks.map((link) => (
            <a key={link.name} href={link.href} className="hover:text-[var(--mint-dark)] transition-colors relative group">
              {link.name}
              <span className="absolute -bottom-1 left-0 w-0 h-[1.5px] bg-[var(--mint)] transition-all duration-300 group-hover:w-full"></span>
            </a>
          ))}
        </div>

        <div className="flex items-center gap-4 md:gap-6 text-[var(--navy)]">
          <button className="hover:text-[var(--mint-dark)] transition-colors hidden sm:block focus:outline-none" aria-label="Account">
            <SafeIcon icon={FiUser} className="text-xl" />
          </button>
          <button 
            onClick={onCartOpen}
            className="relative hover:text-[var(--mint-dark)] transition-colors group p-2 focus:outline-none"
            aria-label={`Cart with ${cartCount} items`}
          >
            <SafeIcon icon={FiShoppingBag} className="text-xl" />
            {cartCount > 0 && (
              <motion.span 
                initial={{ scale: 0 }} animate={{ scale: 1 }}
                className="absolute top-0 right-0 bg-[var(--navy)] text-[var(--mint)] text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center border-2 border-white"
              >
                {cartCount}
              </motion.span>
            )}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsMobileMenuOpen(false)} className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60]" />
            <motion.div initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }} className="fixed top-0 left-0 h-full w-[80%] max-w-sm bg-white z-[70] p-8 shadow-2xl">
              <div className="flex justify-between items-center mb-12">
                <span className="text-xl font-bold italic">Menu</span>
                <button onClick={() => setIsMobileMenuOpen(false)} className="p-2"><SafeIcon icon={FiX} className="text-2xl" /></button>
              </div>
              <div className="flex flex-col gap-8">
                {navLinks.map((link) => (
                  <a key={link.name} href={link.href} onClick={() => setIsMobileMenuOpen(false)} className="text-3xl font-bold text-[var(--navy)] hover:text-[var(--mint-dark)]">{link.name}</a>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}